import { useState, useRef, useCallback, useEffect } from 'react';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaLock, FaShieldAlt, FaRupeeSign, FaPlus, FaCheck } from 'react-icons/fa';

function PaymentModal({ isOpen, onClose, amount = 999, productName = "Credit Control Coaching" }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const paymentInitiated = useRef(false);
  
  // Bundle states
  const [availableBundles, setAvailableBundles] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  
  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || 'http://localhost:5005/api';
  const PAGE_URL = 'https://lp1.rushneeksingh.com/credit-control';
  
  // Calculate total amount
  const calculateTotalAmount = useCallback(() => {
    const bundleTotal = selectedBundles.reduce((total, bundleId) => {
      const bundle = availableBundles.find(b => b.id === bundleId);
      return total + (bundle ? parseFloat(bundle.price) : 0);
    }, 0);
    return amount + bundleTotal;
  }, [amount, selectedBundles, availableBundles]);

  // Fetch available bundles from database
  const fetchBundles = useCallback(async () => {
    try {
      setLoadingBundles(true);
      const response = await fetch(`${API_BASE_URL}/payments/bundles/available?page=credit-control`);
      const data = await response.json();
      if (data.success) {
        // Filter out the current product from bundles
        const filteredBundles = data.bundles.filter(b => 
          !b.name.toLowerCase().includes('credit control')
        );
        setAvailableBundles(filteredBundles);
      }
    } catch (err) {
      console.error('Failed to fetch bundles:', err);
    } finally {
      setLoadingBundles(false);
    }
  }, [API_BASE_URL]);

  // Fetch bundles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBundles();
      setSelectedBundles([]);
    }
  }, [isOpen, fetchBundles]);

  // Toggle bundle selection
  const toggleBundle = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId) 
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
  };

  // Get selected bundles data
  const getSelectedBundlesData = useCallback(() => {
    return selectedBundles.map(id => {
      const bundle = availableBundles.find(b => b.id === id);
      return bundle ? { id: bundle.id, name: bundle.name, price: parseFloat(bundle.price), link: bundle.link } : null;
    }).filter(Boolean);
  }, [selectedBundles, availableBundles]);

  // Handle modal close
  const handleModalClose = useCallback(async () => {
    const hasContactInfo = formData.email.trim() || formData.phone.trim();
    
    if (hasContactInfo && !paymentInitiated.current) {
      try {
        await fetch(`${API_BASE_URL}/payments/pending`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name || 'Unknown',
            email: formData.email || '',
            phone: formData.phone || '',
            amount: calculateTotalAmount(),
            productName: productName,
            pageUrl: PAGE_URL
          })
        });
      } catch (err) {
        console.error('Failed to save pending payment:', err);
      }
    }
    setFormData({ name: '', email: '', phone: '' });
    setErrors({});
    setSelectedBundles([]);
    paymentInitiated.current = false;
    onClose();
  }, [formData, productName, onClose, API_BASE_URL, calculateTotalAmount]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit Indian phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    const totalAmount = calculateTotalAmount();
    const bundlesData = getSelectedBundlesData();

    const isLoaded = await loadRazorpayScript();
    
    if (!isLoaded) {
      alert('Failed to load Razorpay. Please check your internet connection and try again.');
      setIsLoading(false);
      return;
    }

    // Create payment record in backend first
    let paymentId = null;
    try {
      const createResponse = await fetch(`${API_BASE_URL}/payments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          amount: totalAmount,
          productName: productName,
          bundles: bundlesData
        })
      });
      const createData = await createResponse.json();
      if (createData.success) {
        paymentId = createData.paymentId;
        paymentInitiated.current = true;
      }
    } catch (err) {
      console.error('Failed to create payment record:', err);
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_xxxxxxxxxxxxxxx',
      amount: totalAmount * 100,
      currency: 'INR',
      name: 'Rushneek Singh',
      description: productName + (bundlesData.length > 0 ? ` + ${bundlesData.length} Add-ons` : ''),
      image: 'https://lp1.rushneeksingh.com/static/media/logo.png',
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        product: productName,
        paymentId: paymentId,
        bundles: JSON.stringify(bundlesData)
      },
      theme: {
        color: '#F59E0B',
        backdrop_color: 'rgba(15, 23, 42, 0.9)'
      },
      modal: {
        ondismiss: async function() {
          try {
            await fetch(`${API_BASE_URL}/payments/pending`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paymentId,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                amount: totalAmount,
                productName: productName,
                pageUrl: PAGE_URL
              })
            });
          } catch (err) {
            console.error('Failed to record pending payment:', err);
          }
          setIsLoading(false);
        }
      },
      handler: async function(response) {
        try {
          await fetch(`${API_BASE_URL}/payments/success`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              amount: totalAmount,
              productName: productName,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              payment_method: 'razorpay',
              bundles: bundlesData
            })
          });
        } catch (err) {
          console.error('Failed to record successful payment:', err);
        }
        
        alert(`Payment Successful!\n\nPayment ID: ${response.razorpay_payment_id}\n\nThank you for your purchase, ${formData.name}! You will receive a confirmation email at ${formData.email} shortly.`);
        setIsLoading(false);
        paymentInitiated.current = false;
        setFormData({ name: '', email: '', phone: '' });
        setSelectedBundles([]);
        onClose();
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async function(response) {
        try {
          await fetch(`${API_BASE_URL}/payments/failed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              amount: totalAmount,
              productName: productName,
              error_code: response.error.code,
              error_description: response.error.description,
              pageUrl: PAGE_URL
            })
          });
        } catch (err) {
          console.error('Failed to record failed payment:', err);
        }
        
        alert(`Payment Failed!\n\nReason: ${response.error.description}\n\nDon't worry! We've saved your information and sent you an email with a link to try again.\n\nYou can also retry payment anytime by visiting the course page.`);
        setIsLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('Razorpay error:', error);
      alert('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"
        onClick={handleModalClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl shadow-2xl shadow-black/50 border border-amber-500/20 animate-[fadeIn_0.3s_ease-out]">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500"></div>
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl"></div>

        {/* Close Button */}
        <button
          onClick={handleModalClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white transition-all duration-200 z-10"
        >
          <FaTimes className="text-lg" />
        </button>

        {/* Content */}
        <div className="relative p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl mb-3 shadow-lg shadow-amber-500/30">
              <FaLock className="text-xl text-slate-900" />
            </div>
            <h2 className="text-2xl font-black text-white mb-1">
              Secure Checkout
            </h2>
            <p className="text-slate-400 font-medium text-sm">
              {productName}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handlePayment} className="space-y-4">
            {/* Name Field */}
            <div className="relative">
              <label className="block text-sm font-semibold text-slate-300 mb-1.5">
                Full Name *
              </label>
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`w-full bg-slate-700/50 border ${errors.name ? 'border-red-500' : 'border-slate-600'} rounded-xl px-11 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="relative">
              <label className="block text-sm font-semibold text-slate-300 mb-1.5">
                Email Address *
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className={`w-full bg-slate-700/50 border ${errors.email ? 'border-red-500' : 'border-slate-600'} rounded-xl px-11 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="relative">
              <label className="block text-sm font-semibold text-slate-300 mb-1.5">
                Phone Number *
              </label>
              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  className={`w-full bg-slate-700/50 border ${errors.phone ? 'border-red-500' : 'border-slate-600'} rounded-xl px-11 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-400">{errors.phone}</p>
              )}
            </div>

            {/* Add-on Bundles Section */}
            {availableBundles.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <FaPlus className="text-amber-400" />
                  <span className="text-sm font-bold text-white">Add More Courses (Optional)</span>
                </div>
                <div className="space-y-2">
                  {availableBundles.map(bundle => (
                    <label 
                      key={bundle.id}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedBundles.includes(bundle.id)
                          ? 'bg-amber-500/20 border-2 border-amber-500'
                          : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all ${
                        selectedBundles.includes(bundle.id)
                          ? 'bg-amber-500 text-slate-900'
                          : 'bg-slate-600 border border-slate-500'
                      }`}>
                        {selectedBundles.includes(bundle.id) && <FaCheck className="text-xs" />}
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedBundles.includes(bundle.id)}
                        onChange={() => toggleBundle(bundle.id)}
                        className="hidden"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-white leading-tight">{bundle.name}</p>
                      </div>
                      <span className="text-amber-400 font-bold text-sm whitespace-nowrap">
                        +₹{parseFloat(bundle.price).toLocaleString('en-IN')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {loadingBundles && (
              <div className="text-center py-2">
                <span className="text-slate-400 text-sm">Loading add-ons...</span>
              </div>
            )}

            {/* Price Summary */}
            {(() => {
              const totalAmount = calculateTotalAmount();
              const bundleTotal = totalAmount - amount;
              return (
                <div className="mt-4 pt-4 border-t border-slate-700 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">{productName}</span>
                    <span className="text-white">₹{amount.toLocaleString('en-IN')}</span>
                  </div>
                  {bundleTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Add-ons ({selectedBundles.length})</span>
                      <span className="text-amber-400">+₹{bundleTotal.toLocaleString('en-IN')}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-slate-600">
                    <span className="text-white font-bold">Total</span>
                    <div className="flex items-center gap-1">
                      <FaRupeeSign className="text-amber-400 text-lg" />
                      <span className="text-2xl font-black text-amber-400">{totalAmount.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-slate-900 py-4 rounded-xl text-lg font-extrabold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-amber-500/30 flex items-center justify-center gap-3"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <FaShieldAlt className="text-xl" />
                  Pay ₹{calculateTotalAmount().toLocaleString('en-IN')} Securely
                </>
              )}
            </button>
          </form>

          {/* Trust Badges */}
          <div className="mt-4 flex items-center justify-center gap-4 text-slate-500 text-xs">
            <div className="flex items-center gap-1">
              <FaShieldAlt className="text-green-500" />
              <span>100% Secure</span>
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <FaLock className="text-green-500" />
              <span>SSL Encrypted</span>
            </div>
          </div>

          {/* Powered By */}
          <div className="mt-3 text-center">
            <p className="text-slate-600 text-xs">
              Powered by <span className="font-semibold text-slate-500">Razorpay</span>
            </p>
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
}

export default PaymentModal;
